"use client";

import type { AnchorHTMLAttributes } from "react";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";
import {
  usePathname,
  useRouter,
  useSearchParams as useNextSearchParams,
} from "next/navigation";
import { forwardRef, useEffect } from "react";

type NavigateOptions = {
  replace?: boolean;
};

type RouterState = {
  pathname: string;
  search: string;
  hash: string;
};

export interface LinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">,
    Omit<NextLinkProps, "href"> {
  to: NextLinkProps["href"];
}

export interface NavLinkRenderState {
  isActive: boolean;
  isPending: boolean;
}

export interface NavLinkProps extends Omit<LinkProps, "className"> {
  className?: string | ((state: NavLinkRenderState) => string);
  end?: boolean;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, ...props },
  ref,
) {
  return <NextLink ref={ref} href={to} {...props} />;
});

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  { to, className, end, ...props },
  ref,
) {
  const pathname = usePathname();
  const target = typeof to === "string" ? to : String(to);
  const isActive = end ? pathname === target : pathname === target || pathname.startsWith(`${target}/`);

  return (
    <Link
      ref={ref}
      to={to}
      className={typeof className === "function" ? className({ isActive, isPending: false }) : className}
      {...props}
    />
  );
});

export function useNavigate() {
  const router = useRouter();

  return (to: string, options?: NavigateOptions) => {
    if (options?.replace) {
      router.replace(to);
      return;
    }
    router.push(to);
  };
}

export function useLocation(): RouterState {
  const pathname = usePathname();
  const searchParams = useNextSearchParams();
  const search = searchParams.toString();

  return {
    pathname,
    search: search ? `?${search}` : "",
    hash: typeof window !== "undefined" ? window.location.hash : "",
  };
}

export function useSearchParams(): [ReturnType<typeof useNextSearchParams>] {
  return [useNextSearchParams()];
}

export function Navigate({
  to,
  replace,
}: {
  to: string;
  replace?: boolean;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(to, { replace });
  }, [navigate, replace, to]);

  return null;
}
